<template>
  <div>
    <div v-if="sttusCode">
      <div v-if="!step && sttusCode === 'migration_status.none'">
        <div class="migration-button-area">
          <button class="btn btn-primary" @click="startMigration()">
            {{ $t('button.nm.start_migration') }}
          </button>
        </div>
        <div v-if="latestMigrationStatusMessage" class="latest-migration-status">
          <span class="latest-migration-status-title">
            {{ $t('migration.title.latest_status') }}
          </span>
          <span class="latest-migration-status-content">
            : {{ latestMigrationStatusMessage }}
          </span>
        </div>
        <div v-if="latestMigrationResultMessage" class="latest-migration-result">
          <span class="latest-migration-result-title">
            {{ $t('migration.title.latest_result') }}
          </span>
          <pre class="latest-migration-result-content">{{ latestMigrationResultMessage.trim() }}</pre>
        </div>
        <list
          module="migration"
          item="migration"
          delete-url="migration/cancelMigrations.json"
          :list-notifications="[{multilanguage: true, value: 'migration.guide.description'}]"
          :custom-item-action-controls="migrationAdditionalControls"
          @clone-migration="cloneMigrationConfirm"
          @migration-rules-excel-download="migrationRulesExcelDownload"
        />
      </div>
      <div v-else-if="sttusCode === 'migration_status.writing_information' || step" class="writing-information-area">
        <div v-if="step === 'intro'" class="step-area">
          <div class="start-migration-guide">
            {{ $t('guide.text.start_migration') }}
          </div>
        </div>
        <div v-else-if="step === 'database-information'" class="step-area" step="database-information">
          <div class="step-title">
            {{ $t('migration.title.database_information') }}
          </div>
          <div class="step-guide">
            {{ $t('migration.guide.database_information') }}
          </div>
          <div class="step-content">
            <div v-if="migrationInformation.originalDatabaseInformations.length === 0" class="no-database">
              {{ $t('migration.notification.more_than_one_db_required') }}
            </div>
            <div v-else>
              <input-control
                v-model="migrationInformation.deleteDataBeforeFlag"
                :title="$t('column.nm.delete_data_before_flag')"
                :name="'delete-data-before-flag-' + migrationInformation.id + '-deleteDataBeforeFlag'"
                type="checkbox"
              />
            </div>
            <div v-for="(databaseInformation, databaseInformationIndex) in migrationInformation.originalDatabaseInformations" :key="databaseInformationIndex" class="original-database-list-item" :database-index="databaseInformationIndex" :connection-validated="databaseInformation.validated">
              <input autocomplete="false" style="display:none" type="text" name="faketextremembered">
              <input autocomplete="false" style="display:none" type="password" name="fakepasswordremembered">
              <input-control
                v-model="databaseInformation.databaseAlias"
                desc="database-alias"
                :title="$t('migration.label.original_database_alias')"
                :name="'database-alias-' + databaseInformationIndex"
                :notifications="['migration.guide.original_database_alias']"
                :required="true"
                :readonly="databaseInformation.validated === 'success'"
                type="text"
              />
              <input-control
                v-model="databaseInformation.databaseTyCode"
                :title="$t('migration.label.original_database_type')"
                :name="'database-type-' + databaseInformationIndex"
                :options="databaseTypeOptions"
                :required="true"
                :readonly="databaseInformation.validated === 'success'"
                type="select"
              />
              <input-control
                v-model="databaseInformation.databaseHost"
                :title="$t('migration.label.original_database_host')"
                :name="'database-host-' + databaseInformationIndex"
                :required="true"
                :readonly="databaseInformation.validated === 'success'"
                type="text"
              />
              <input-control
                v-model="databaseInformation.databasePort"
                :title="$t('migration.label.original_database_port')"
                :name="'database-port-' + databaseInformationIndex"
                :required="true"
                :readonly="databaseInformation.validated === 'success'"
                type="text"
              />
              <input-control
                v-model="databaseInformation.databaseDbName"
                :title="$t('migration.label.original_database_db_name')"
                :name="'database-name-' + databaseInformationIndex"
                :required="true"
                :readonly="databaseInformation.validated === 'success'"
                type="text"
              />
              <input-control
                v-model="databaseInformation.databaseAditParamtr"
                :title="$t('migration.label.original_database_extended_parameters')"
                :name="'database-extended-parameters-' + databaseInformationIndex"
                :readonly="databaseInformation.validated === 'success'"
                type="text"
              />
              <input-control
                v-model="databaseInformation.databaseUserId"
                :title="$t('migration.label.original_database_user_id')"
                :name="'database-user-id-' + databaseInformationIndex"
                :required="true"
                :readonly="databaseInformation.validated === 'success'"
                type="text"
              />
              <input-control
                v-model="databaseInformation.databasePasswd"
                :title="$t('migration.label.original_database_password')"
                :name="'database-password-' + databaseInformationIndex"
                :required="true"
                :readonly="databaseInformation.validated === 'success'"
                type="text"
              />
              <div class="item-button-area">
                <b-badge v-if="databaseInformation.testingConnection" variant="secondary">
                  {{ $t('migration.text.connection_testing') }}
                </b-badge>
                <b-badge v-else-if="databaseInformation.validated === 'success'" variant="success">
                  {{ $t('migration.text.connection_success') }}
                </b-badge>
                <b-badge v-else-if="databaseInformation.validated === 'fail'" variant="danger">
                  {{ $t('migration.text.connection_fail') }}
                </b-badge>
                <b-badge v-else-if="!databaseInformation.id" variant="warning">
                  {{ $t('migration.text.connection_untested') }}
                </b-badge>
                <button
                  class="btn btn-primary test-connection-button"
                  :disabled="databaseInformation.validated === 'success'"
                  @click="testConnection($event, databaseInformation, databaseInformationIndex)"
                >
                  {{ $t('button.nm.test_connection') }}
                </button>
                <button
                  v-if="databaseInformation.validated === 'success'"
                  class="btn btn-warning reenter-button"
                  @click="reenter(databaseInformation)"
                >
                  {{ $t('migration.button.reenter') }}
                </button>
                <button class="btn btn-danger" @click="deleteDatabase($event, databaseInformationIndex)">
                  {{ $t('migration.button.delete_this_database') }}
                </button>
                <div v-if="!databaseInformation.id && databaseInformation.testConnectionMsg" class="test-connection-result">
                  {{ databaseInformation.testConnectionMsg }}
                </div>
              </div>
            </div>
            <button class="form-control btn btn-primary" @click="addDatabase($event)">
              {{ $t('migration.button.add_database') }}
            </button>
          </div>
        </div>
        <div v-else-if="step === 'column-information'" class="step-area" step="column-information">
          <div class="step-title">
            {{ $t('migration.title.migration_information') }}
          </div>
          <div class="step-guide">
            {{ $t('migration.guide.migration_information') }}
          </div>
          <div class="step-content">
            <div v-if="migrationInformation.destinationTableInformations.length === 0" class="no-table">
              {{ $t('migration.notification.more_than_one_table_required') }}
            </div>
            <div v-for="(tableInformation, tableInformationIndex) in migrationInformation.destinationTableInformations" :key="tableInformationIndex" class="table-list-item" :table-index="tableInformationIndex">
              <div class="table-title" @click="toggleExpandTable($event, tableInformation)">
                {{ tableInformation.newTableNm ? tableInformation.newTableNm : $t('migration.text.no_selected_table') }}
                <span class="expand-toggle-button">
                  <i v-if="!tableInformation.expand" class="fa fa-chevron-down" />
                  <i v-else class="fa fa-chevron-up" />
                </span>
              </div>
              <div v-if="tableInformation.expand || tableInformation.validating" v-show="tableInformation.expand" class="table-detail-area">
                <input autocomplete="false" style="display:none" type="text" name="faketextremembered">
                <input autocomplete="false" style="display:none" type="password" name="fakepasswordremembered">
                <input-control
                  v-model="tableInformation.newTableNm"
                  :value="tableInformation.tableNm"
                  :title="$t('column.nm.table_nm')"
                  :name="'table-name-' + tableInformationIndex"
                  :required="true"
                  type="select"
                  :options="destinationTableOptions"
                  @change="onChangeTable(tableInformation)"
                />
                <div v-if="tableInformation.tableNm">
                  <label>{{ $t('migration.text.column_list') }}</label>
                  <div v-if="!tableInformation.destinationTableColumnInformations || tableInformation.destinationTableColumnInformations.length === 0" class="no-column">
                    {{ $t('migration.notification.more_than_one_column_required') }}
                  </div>
                  <div v-for="(columnInformation, columnInformationIndex) in tableInformation.destinationTableColumnInformations" v-else-if="tableInformation.destinationTableColumnInformations" :key="columnInformationIndex" class="column-list-item" :column-index="columnInformationIndex">
                    <div class="column-title" @click="toggleExpandColumn($event, columnInformation)">
                      {{ columnInformation.fileFlag === 'Y' ? $t('migration.text.no_selected_column') : (columnInformation.dstnColumnNm ? columnInformation.dstnColumnNm : $t('migration.text.no_selected_column')) }}
                      <span class="expand-toggle-button">
                        <i v-if="!columnInformation.expand" class="fa fa-chevron-down" />
                        <i v-else class="fa fa-chevron-up" />
                      </span>
                    </div>
                    <div v-if="columnInformation.expand || tableInformation.validating" v-show="columnInformation.expand">
                      <label class="column-information-subtitle">
                        {{ $t('migration.label.destination_information') }}
                      </label>
                      <div class="column-destination-information">
                        <input-control
                          v-model="columnInformation.fileFlag"
                          :title="$t('column.nm.file_flag')"
                          :name="'file-flag-' + tableInformationIndex + '-' + columnInformationIndex + '-fileFlag'"
                          type="checkbox"
                          @change="onChangeColumn(tableInformation, columnInformation)"
                        />
                        <div v-if="columnInformation.fileFlag === 'Y'">
                          <!-- 파일인 경우 -->
                          <input autocomplete="false" style="display:none" type="text" name="faketextremembered">
                          <input autocomplete="false" style="display:none" type="password" name="fakepasswordremembered">
                        </div>
                        <div v-else>
                          <!-- 파일이 아닌 경우 -->
                          <input autocomplete="false" style="display:none" type="text" name="faketextremembered">
                          <input autocomplete="false" style="display:none" type="password" name="fakepasswordremembered">
                          <input-control
                            v-model="columnInformation.staticValue"
                            :title="$t('column.nm.static_value')"
                            :name="'static-value-' + tableInformationIndex + '-' + columnInformationIndex"
                            :notifications="['migration.guide.static_value']"
                            type="text"
                            @input="$forceUpdate()"
                          />
                          <input-control
                            v-model="columnInformation.dstnColumnNm"
                            :title="$t('column.nm.dstn_column_nm')"
                            :name="'destination-column-name-' + tableInformationIndex + '-' + columnInformationIndex"
                            :required="true"
                            type="select"
                            :options="getDestinationTableColumnOptions(tableInformation, columnInformation.dstnColumnNm)"
                            @change="onChangeColumn(tableInformation, columnInformation)"
                          />
                          <input-control
                            v-model="columnInformation.dstnEncptMthCode"
                            :title="$t('column.nm.dstn_encpt_mth_code')"
                            :name="'destination-encryption-method-code-' + tableInformationIndex + '-' + columnInformationIndex"
                            :required="true"
                            type="select"
                            :options="encryptionMethodOptions"
                            @change="$forceUpdate()"
                          />
                        </div>
                      </div>
                      <div v-if="!columnInformation.staticValue">
                        <label class="column-information-subtitle">
                          {{ $t('migration.label.source_information') }}
                        </label>
                        <div class="column-source-information">
                          <input autocomplete="false" style="display:none" type="text" name="faketextremembered">
                          <input autocomplete="false" style="display:none" type="password" name="fakepasswordremembered">
                          <input-control
                            v-model="columnInformation.migrationOrginlDatabaseId"
                            :title="$t('column.nm.migration_orginl_database_id')"
                            :name="'migration-original-database-' + tableInformationIndex + '-' + columnInformationIndex"
                            :required="true"
                            type="select"
                            :options="databaseOptions"
                            @change="$forceUpdate()"
                          />
                          <input-control
                            v-model="columnInformation.newSrcTableNm"
                            :value="columnInformation.srcTableNm"
                            :title="$t('column.nm.src_table_nm')"
                            :name="'source-table-name-' + tableInformationIndex + '-' + columnInformationIndex"
                            :required="true"
                            type="select"
                            :options="originalDatabaseTableOptions[columnInformation.migrationOrginlDatabaseId]"
                            @change="onChangeSourceTable(columnInformation)"
                          />
                          <div v-if="columnInformation.srcTableNm">
                            <input autocomplete="false" style="display:none" type="text" name="faketextremembered">
                            <input autocomplete="false" style="display:none" type="password" name="fakepasswordremembered">
                            <input-control
                              v-model="columnInformation.srcTableAlias"
                              :title="$t('column.nm.src_table_alias')"
                              :name="'source-table-alias-' + tableInformationIndex + '-' + columnInformationIndex"
                              type="text"
                            />
                            <input-control
                              v-model="columnInformation.srcColumnNm"
                              :title="$t('column.nm.src_column_nm')"
                              :name="'source-column-name-' + tableInformationIndex + '-' + columnInformationIndex"
                              type="select"
                              :options="originalDatabaseTableColumnOptions[columnInformation.migrationOrginlDatabaseId][columnInformation.srcTableNm]"
                            />
                            <input-control
                              v-if="!columnInformation.srcColumnNm"
                              v-model="columnInformation.srcColumnCustomQuery"
                              :title="$t('column.nm.src_column_custom_query')"
                              :name="'source-column-custom-query-' + tableInformationIndex + '-' + columnInformationIndex"
                              :required="true"
                              type="text"
                            />
                            <input-control
                              v-if="columnInformation.srcColumnCustomQuery"
                              v-model="columnInformation.srcColumnAlias"
                              :title="$t('column.nm.src_column_alias')"
                              :name="'source-column-alias1-' + tableInformationIndex + '-' + columnInformationIndex"
                              :required="true"
                              type="text"
                            />
                            <input-control
                              v-else
                              v-model="columnInformation.srcColumnAlias"
                              :title="$t('column.nm.src_column_alias')"
                              :name="'source-column-alias2-' + tableInformationIndex + '-' + columnInformationIndex"
                              type="text"
                            />
                            <div v-if="columnInformation.fileFlag === 'Y'">
                              <input-control
                                v-model="columnInformation.mimeColumnNm"
                                :title="$t('column.nm.mime_column_nm')"
                                :name="'mime-column-name-' + tableInformationIndex + '-' + columnInformationIndex"
                                type="select"
                                :options="originalDatabaseTableColumnOptions[columnInformation.migrationOrginlDatabaseId][columnInformation.srcTableNm]"
                              />
                              <input-control
                                v-if="!columnInformation.mimeColumnNm"
                                v-model="columnInformation.mimeColumnCustomQuery"
                                :title="$t('column.nm.mime_column_custom_query')"
                                :name="'mime-column-custom-query-' + tableInformationIndex + '-' + columnInformationIndex"
                                :required="true"
                                type="text"
                              />
                              <input-control
                                v-model="columnInformation.fileNmColumnNm"
                                :title="$t('column.nm.file_nm_column_nm')"
                                :name="'file-name-column-name-' + tableInformationIndex + '-' + columnInformationIndex"
                                type="select"
                                :options="originalDatabaseTableColumnOptions[columnInformation.migrationOrginlDatabaseId][columnInformation.srcTableNm]"
                              />
                              <input-control
                                v-if="!columnInformation.fileNmColumnNm"
                                v-model="columnInformation.fileNmColumnCustomQuery"
                                :title="$t('column.nm.file_nm_column_custom_query')"
                                :name="'file-name-column-custom-query-' + tableInformationIndex + '-' + columnInformationIndex"
                                :required="true"
                                type="text"
                              />
                            </div>
                            <input-control
                              v-model="columnInformation.srcEncptMthCode"
                              :title="$t('column.nm.src_encpt_mth_code')"
                              :name="'source-encryption-method-code-' + tableInformationIndex + '-' + columnInformationIndex"
                              :required="true"
                              type="select"
                              :options="decryptionMethodOptions"
                              @change="$forceUpdate()"
                            />
                            <input-control
                              v-if="columnInformation.srcEncptMthCode === 'migration_crypto_method.aes128'"
                              v-model="columnInformation.srcEncptKey"
                              :title="$t('column.nm.src_encpt_key')"
                              :name="'source-encryption-key-' + tableInformationIndex + '-' + columnInformationIndex"
                              type="text"
                            />
                            <input-control
                              v-if="columnInformation.srcEncptMthCode === 'migration_crypto_method.aes128'"
                              v-model="columnInformation.srcEncptTransformation"
                              :title="$t('column.nm.src_encpt_transformation')"
                              :name="'source-encryption-transformation-' + tableInformationIndex + '-' + columnInformationIndex"
                              type="text"
                            />
                            <div v-if="columnInformationIndex === 0">
                              <input-control
                                v-model="columnInformation.srcCustomWhereCnd"
                                :title="$t('column.nm.src_custom_where_cnd')"
                                :name="'source-custom-where-condition-' + tableInformationIndex + '-' + columnInformationIndex"
                                type="text"
                              />
                            </div>
                            <div v-if="joinRequired(tableInformation, columnInformation, columnInformationIndex)">
                              <div v-if="differentDatabaseTableExist(tableInformation, columnInformation)">
                                <input-control
                                  v-model="columnInformation.srcWhereCndColumnNm"
                                  :title="$t('column.nm.src_where_cnd_column_nm')"
                                  :name="'source-where-condition-column-name-' + tableInformationIndex + '-' + columnInformationIndex"
                                  type="select"
                                  :required="true"
                                  :options="originalDatabaseTableColumnOptions[columnInformation.migrationOrginlDatabaseId][columnInformation.srcTableNm]"
                                />
                                <input-control
                                  v-model="columnInformation.srcWhereCndValue"
                                  :title="$t('column.nm.src_where_cnd_value')"
                                  :name="'source-where-condition-value-' + tableInformationIndex + '-' + columnInformationIndex"
                                  :options="predefinedColumnOptions(tableInformation, columnInformationIndex)"
                                  type="select"
                                  :required="true"
                                />
                              </div>
                              <div v-else>
                                <input-control
                                  v-model="columnInformation.srcWhereCndColumnNm"
                                  :title="$t('column.nm.src_where_cnd_column_nm')"
                                  :name="'source-where-condition-column-name-' + tableInformationIndex + '-' + columnInformationIndex"
                                  type="select"
                                  :options="originalDatabaseTableColumnOptions[columnInformation.migrationOrginlDatabaseId][columnInformation.srcTableNm]"
                                />
                                <input-control
                                  v-model="columnInformation.srcWhereCndValue"
                                  :title="$t('column.nm.src_where_cnd_value')"
                                  :name="'source-where-condition-value-' + tableInformationIndex + '-' + columnInformationIndex"
                                  :options="predefinedColumnOptions(tableInformation, columnInformationIndex)"
                                  type="select"
                                />
                              </div>
                              <input-control
                                v-model="columnInformation.srcJoinCnd"
                                :title="$t('column.nm.src_join_cnd')"
                                :name="'source-join-condition-' + tableInformationIndex + '-' + columnInformationIndex"
                                type="text"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="item-button-area">
                        <button class="btn btn-danger" @click="deleteColumn($event, tableInformation, columnInformationIndex)">
                          {{ $t('migration.button.delete_this_column') }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <button class="form-control btn btn-primary" @click="addColumn($event, tableInformation)">
                    {{ $t('migration.button.add_column') }}
                  </button>
                </div>
                <div class="expand-advanced-button-area">
                  <button class="btn btn-secondary" @click="toggleExpandAdvancedTable($event, tableInformation)">
                    {{ $t('migration.button.advanced') }}
                    <i v-if="!tableInformation.expandAdvanced" class="fa fa-chevron-down" />
                    <i v-else class="fa fa-chevron-up" />
                  </button>
                </div>
                <div v-if="tableInformation.expandAdvanced" class="table-advanced-area">
                  <label>
                    {{ $t('column.nm.pre_query') }}
                  </label>
                  <div class="query-area">
                    <div v-if="tableInformation.prequeries && tableInformation.prequeries.length > 0">
                      <div v-for="(query, queryIndex) in tableInformation.prequeries" :key="queryIndex" class="query-item">
                        <div class="input-group">
                          <input v-if="!query.multiline" v-model="query.text" type="text" class="form-control">
                          <textarea v-else v-model="query.text" class="form-control query-textarea" @input="onInputQuery($event)" />
                          <span class="input-group-btn">
                            <button class="btn btn-secondary" @click="toggleTextMode($event, query)">
                              {{ $t('migration.button.change_text_mode') }}
                            </button>
                            <button class="btn btn-danger" @click="deleteQuery($event, tableInformation.prequeries, queryIndex)">
                              {{ $t('migration.button.delete_this_query') }}
                            </button>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div v-else class="no-query">
                      {{ $t('migration.notification.no_query') }}
                    </div>
                    <div class="add-query-area">
                      <button class="form-control btn btn-primary" @click="addQuery($event, tableInformation, 'prequeries')">
                        {{ $t('migration.button.add_query') }}
                      </button>
                    </div>
                  </div>
                  <label>
                    {{ $t('column.nm.post_query') }}
                  </label>
                  <div class="query-area">
                    <div v-if="tableInformation.postqueries && tableInformation.postqueries.length > 0">
                      <div v-for="(query, queryIndex) in tableInformation.postqueries" :key="queryIndex" class="query-item">
                        <div class="input-group">
                          <input v-if="!query.multiline" v-model="query.text" type="text" class="form-control">
                          <textarea v-else v-model="query.text" class="form-control query-textarea" @input="onInputQuery($event)" />
                          <span class="input-group-btn">
                            <button class="btn btn-secondary" @click="toggleTextMode($event, query)">
                              {{ $t('migration.button.change_text_mode') }}
                            </button>
                            <button class="btn btn-danger" @click="deleteQuery($event, tableInformation.postqueries, queryIndex)">
                              {{ $t('migration.button.delete_this_query') }}
                            </button>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div v-else class="no-query">
                      {{ $t('migration.notification.no_query') }}
                    </div>
                    <div class="add-query-area">
                      <button class="form-control btn btn-primary" @click="addQuery($event, tableInformation, 'postqueries')">
                        {{ $t('migration.button.add_query') }}
                      </button>
                    </div>
                  </div>
                </div>
                <div class="item-button-area">
                  <button class="btn btn-danger" @click="deleteTable($event, tableInformationIndex)">
                    {{ $t('migration.button.delete_this_table') }}
                  </button>
                </div>
              </div>
            </div>
            <button class="form-control btn btn-primary" @click="addTable($event)">
              {{ $t('migration.button.add_table') }}
            </button>
          </div>
        </div>
        <div v-if="step && step !== 'intro'" class="step-control-area form-actions">
          <div class="excel-button-area">
            <div v-if="validating && step === 'column-information'" class="validation-status">
              {{ validatingTable }} / {{ validatingColumn }}
            </div>
            <b-button @click="showMigrationRulesExcelUpload">
              {{ $t('migration.button.migration_rules_excel_upload') }}
            </b-button>
            <b-button v-b-modal.migration-rules-excel-download-confirm-modal :disabled="!migrationInformation.originalDatabaseInformations || migrationInformation.originalDatabaseInformations.length === 0">
              {{ $t('migration.button.migration_rules_excel_download') }}
            </b-button>
          </div>
          <div class="row">
            <div class="col-sm-6 previous-button-area">
              <button v-if="step !== 'database-information'" class="btn btn-primary" :disabled="!canGoPrevious()" @click="gotoPreviousPreprocess()">
                {{ $t('migration.button.previous') }}
              </button>
            </div>
            <div class="col-sm-6 next-button-area">
              <b-button variant="primary" :disabled="!migrationInformation.originalDatabaseInformations || migrationInformation.originalDatabaseInformations.length === 0" @click="savePreprocess()">
                {{ $t('common.button.save') }}
              </b-button>
              <b-button v-b-modal.cancel-migration-confirm-modal>
                {{ $t('common.button.cancel') }}
              </b-button>
              <b-button variant="primary" :disabled="!canGoNext()" @click="gotoNextPreprocess()">
                {{ $t('migration.button.next') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="sttusCode === 'migration_status.in_progress'">
        <div v-if="lastMigration">
          <div class="migration-status">
            {{ $t('code_group.nm.migration_status') }} : {{ $t('code.nm.' + lastMigration.sttusCode) }}<span v-if="lastMigration.sttusMsg">({{ lastMigration.sttusMsg }})</span>
            <b-progress :max="100">
              <b-progress-bar :value="getProcessedRate(lastMigration)" :label="`${(getProcessedRate(lastMigration)).toFixed(2)}%`" />
            </b-progress>
          </div>
          <div v-for="(table, tableIndex) in lastMigration.destinationTableInformations" :key="tableIndex">
            {{ table.tableNm }}
            <span v-if="table.processCo">: {{ table.processCo }}/{{ table.allCo }} {{ $t('code.nm.' + table.sttusCode) }}<span v-if="table.sttusMsg">({{ table.sttusMsg }})</span></span>
            <b-progress v-if="table.processCo" :max="100">
              <b-progress-bar :value="100 * table.processCo / table.allCo" :label="`${((table.processCo / table.allCo) * 100).toFixed(2)}%`" />
            </b-progress>
          </div>
        </div>
      </div>
    </div>
    <b-modal
      id="change-table-confirm-modal"
      ref="changeTableConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="changeTable"
      @cancel="cancelChangeTable"
    >
      <p class="my-4" v-html="$t('migration.modal.text.change_table_confirm')" />
    </b-modal>
    <b-modal
      id="change-source-table-confirm-modal"
      ref="changeSourceTableConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="changeSourceTable"
      @cancel="cancelChangeSourceTable"
    >
      <p class="my-4" v-html="$t('migration.modal.text.change_table_confirm')" />
    </b-modal>
    <b-modal
      id="reenter-database-confirm-modal"
      ref="reenterDatabaseConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="reenterDatabaseProcess"
    >
      <p class="my-4" v-html="$t('migration.modal.text.reenter_database_confirm')" />
    </b-modal>
    <b-modal
      id="delete-database-confirm-modal"
      ref="deleteDatabaseConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="deleteDatabaseProcess"
    >
      <p class="my-4" v-html="$t('migration.modal.text.delete_database_confirm')" />
    </b-modal>
    <b-modal
      id="cancel-migration-confirm-modal"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="cancelMigrationProcess"
    >
      <p class="my-4" v-html="$t('migration.modal.text.cancel_migration_confirm')" />
    </b-modal>
    <b-modal
      id="delete-table-confirm-modal"
      ref="deleteTableConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="deleteTableProcess"
    >
      <p class="my-4" v-html="$t('common.modal.text.delete_confirm')" />
    </b-modal>
    <b-modal
      id="migrate-confirm-modal"
      ref="migrateConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="migrateProcess"
    >
      <p class="my-4" v-html="$t('migration.modal.text.migrate_confirm')" />
    </b-modal>
    <b-modal
      id="clone-migration-confirm-modal"
      ref="cloneMigrationConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="cloneMigration"
    >
      <p class="my-4" v-html="$t('migration.modal.text.clone_confirm')" />
    </b-modal>
    <b-modal
      id="migration-rules-excel-upload-modal"
      ref="migration-rules-excel-upload-modal"
      :title="$t('migration.button.migration_rules_excel_upload')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      :ok-disabled="!migrationRulesExcelFile"
      @ok="migrationRulesExcelUploadConfirm"
    >
      <file-uploader
        ref="migrationRulesExcelUpload"
        v-model="migrationRulesExcelFile"
        :upload-url="'migration/migrationRulesExcelUpload' + (migrationInformation.id ? ('?migrationId=' + migrationInformation.id):'')"
        polling-url="/file/pollUploadStatus.json"
        accept=".xlsx"
        @on-uploaded="onUploadedRules"
      />
    </b-modal>
    <b-modal
      id="migration-rules-excel-upload-confirm-modal"
      ref="migrationRulesExcelUploadConfirmModal"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="migrationRulesExcelUploadProcess"
    >
      <p class="my-4" v-html="$t('migration.modal.text.migration_rules_excel_upload_confirm')" />
    </b-modal>
    <b-modal
      id="migration-rules-excel-download-confirm-modal"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="saveAndMigrationRulesExcelDownload"
    >
      <p class="my-4" v-html="$t('migration.modal.text.migration_rules_excel_download_confirm')" />
    </b-modal>
  </div>
</template>

<script>
export default {
  data() {
    return {
      sttusCode: '',
      oldSttusCode: '',
      step: '',
      migrationRulesExcelFile: undefined,
      lastMigration: undefined,
      lastFinishedMigration: undefined,
      latestMigrationStatusMessage: '',
      latestMigrationResultMessage: '',
      migrationInformation: {
        originalDatabaseInformations: [],
        destinationTableInformations: []
      },
      migrationAdditionalControls: [
        {
          name: this.$t('migration.button.clone'),
          event: 'clone-migration',
          icon: 'clone'
        },
        {
          name: this.$t('migration.button.migration_rules_excel_download'),
          event: 'migration-rules-excel-download',
          icon: 'download'
        }
      ],
      databaseOptions: [],
      encryptionMethodOptions: [
        {
          text: this.$t('code.nm.migration_crypto_method.none'),
          value: 'migration_crypto_method.none'
        },
        {
          text: this.$t('code.nm.migration_crypto_method.aes128'),
          value: 'migration_crypto_method.aes128'
        },
        {
          text: this.$t('code.nm.migration_crypto_method.sha256'),
          value: 'migration_crypto_method.sha256'
        },
        {
          text: this.$t('code.nm.migration_crypto_method.bcrypt'),
          value: 'migration_crypto_method.bcrypt'
        }
      ],
      decryptionMethodOptions: [
        {
          text: this.$t('code.nm.migration_crypto_method.none'),
          value: 'migration_crypto_method.none'
        },
        {
          text: this.$t('code.nm.migration_crypto_method.aes128'),
          value: 'migration_crypto_method.aes128'
        }
      ],
      databaseTypeOptions: [
        {
          text: '- ' + this.$t('migration.label.original_database_type') + ' -',
          value: ''
        },
        {
          text: this.$t('code.nm.database_type.mysql'),
          value: 'database_type.mysql'
        },
        {
          text: this.$t('code.nm.database_type.mariadb'),
          value: 'database_type.mariadb'
        },
        {
          text: this.$t('code.nm.database_type.mssql'),
          value: 'database_type.mssql'
        }
      ],
      destinationTableOptions: [],
      destinationTableColumnOptions: {},
      originalDatabaseTableOptions: {},
      originalDatabaseTableColumnOptions: {},
      validating: false,
      validatingTable: '',
      validatingColumn: ''
    }
  },
  mounted() {
    this.pollLatestMigration()
  },
  beforeDestroy() {
    if (this.pollTimerId) {
      clearTimeout(this.pollTimerId)
      this.pollTimerId = undefined
    }
  },
  methods: {
    toggleExpandTable(e, table) {
      table.expand = !table.expand
      this.$forceUpdate()
    },
    toggleExpandColumn(e, column) {
      column.expand = !column.expand
      this.$forceUpdate()
    },
    toggleExpandAdvancedTable(e, table) {
      table.expandAdvanced = !table.expandAdvanced
      this.$forceUpdate()
    },
    toggleTextMode(e, query) {
      query.multiline = !query.multiline
      this.$forceUpdate()
      this.$nextTick(() => {
        if (query.multiline) {
          const text = $(e.target)
            .parent()
            .siblings('textarea')
            .get(0)
          window._autoResizeTextArea(text)
        }
      })
    },
    onInputQuery(e) {
      const text = e.target
      window._autoResizeTextArea(text)
    },
    onUploadedRules() {
      this.fileUploaded = true
      this.hideModal('migration-rules-excel-upload-modal')
    },
    getProcessedRate(migration) {
      let rate = 0
      if (migration && migration.destinationTableInformations) {
        const allTablesCo = migration.destinationTableInformations.length
        let processedTablesCo = allTablesCo
        for (let i = 0; i < allTablesCo; i++) {
          const table = migration.destinationTableInformations[i]
          if (table.sttusCode === 'migration_table_status.ready') {
            processedTablesCo--
          } else if (table.sttusCode === 'migration_table_status.in_progress') {
            if (table.allCo) {
              processedTablesCo -= 1 - table.processCo / table.allCo
            } else {
              processedTablesCo--
            }
          }
        }
        rate = (100 * processedTablesCo) / allTablesCo
      }
      return rate
    },
    predefinedColumnOptions(tableInformation, columnInformationIndex) {
      const options = [
        {
          text: this.$t('common.placeholder.select'),
          value: ''
        }
      ]
      const columns = tableInformation.destinationTableColumnInformations
      for (let i = 0; i < columnInformationIndex; i++) {
        if (columns[i].fileFlag === 'Y') {
          continue
        }
        if (columns[i].staticValue) {
          continue
        }
        let dbAlias = ''
        let dbId = 0
        for (let j = 0; j < this.databaseOptions.length; j++) {
          if (
            parseInt(this.databaseOptions[j].value) ===
            parseInt(columns[i].migrationOrginlDatabaseId)
          ) {
            dbAlias = this.databaseOptions[j].text
            dbId = this.databaseOptions[j].value
          }
        }
        let text = ''
        const value =
          dbId +
          ' -=- ' +
          (columns[i].srcTableNm ? columns[i].srcTableNm : '') +
          ' -=- ' +
          (columns[i].srcTableAlias ? columns[i].srcTableAlias : '') +
          ' -=- ' +
          (columns[i].srcColumnAlias ? columns[i].srcColumnAlias : '') +
          ' -=- ' +
          (columns[i].srcColumnNm ? columns[i].srcColumnNm : '') +
          ' -=- ' +
          (columns[i].srcColumnCustomQuery
            ? columns[i].srcColumnCustomQuery
            : '')
        if (columns[i].srcColumnAlias) {
          text += columns[i].srcColumnAlias + ' - '
        }
        if (columns[i].srcColumnCustomQuery) {
          text += columns[i].srcColumnCustomQuery
        } else {
          if (columns[i].srcTableAlias) {
            text += columns[i].srcTableAlias
          } else {
            text += columns[i].srcTableNm
          }
          text += '.'
          text += columns[i].srcColumnNm
        }
        options.push({
          text: text + ' - ' + dbAlias,
          value: value
        })
      }
      return options
    },
    differentDatabaseTableExist(tableInformation, columnInformation) {
      const columns = tableInformation.destinationTableColumnInformations
      let foundDifferent = false
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].fileFlag === 'Y') {
          continue
        }
        if (
          parseInt(columns[i].migrationOrginlDatabaseId) !==
          parseInt(columnInformation.migrationOrginlDatabaseId)
        ) {
          foundDifferent = true
          break
        }
      }
      return foundDifferent
    },
    joinRequired(tableInformation, columnInformation, columnInformationIndex) {
      if (columnInformationIndex === 0) {
        return false
      }
      let foundOtherTable = false
      const columns = tableInformation.destinationTableColumnInformations
      for (let i = 0; i < columns.length; i++) {
        const column = columns[i]
        if (column.staticValue) {
          continue
        }
        if (!column.srcTableNm) {
          column.srcTableNm = ''
        }
        if (!column.srcTableAlias) {
          column.srcTableAlias = ''
        }
        if (!columnInformation.srcTableNm) {
          columnInformation.srcTableNm = ''
        }
        if (!columnInformation.srcTableAlias) {
          columnInformation.srcTableAlias = ''
        }
        if (!column.migrationOrginlDatabaseId) {
          column.migrationOrginlDatabaseId = 0
        }
        if (!columnInformation.migrationOrginlDatabaseId) {
          columnInformation.migrationOrginlDatabaseId = 0
        }
        if (i !== columnInformationIndex) {
          if (
            column.migrationOrginlDatabaseId !==
              columnInformation.migrationOrginlDatabaseId ||
            column.srcTableNm !== columnInformation.srcTableNm ||
            column.srcTableAlias !== columnInformation.srcTableAlias
          ) {
            foundOtherTable = true
            break
          }
        }
      }
      let foundSameTableInFrontOfMe = false
      for (let i = columnInformationIndex - 1; i >= 0; i--) {
        const column = columns[i]
        if (i !== columnInformationIndex) {
          if (
            column.migrationOrginlDatabaseId + '' ===
              columnInformation.migrationOrginlDatabaseId + '' &&
            column.srcTableNm === columnInformation.srcTableNm &&
            column.srcTableAlias === columnInformation.srcTableAlias
          ) {
            foundSameTableInFrontOfMe = true
            break
          }
        }
      }
      // 다른 테이블이 있고, 나와 동일한 이름이 내 앞에 없는 경우는 true 아니면 false
      return foundOtherTable && !foundSameTableInFrontOfMe
    },
    getDestinationTableColumnOptions(tableInformation, me) {
      const options = []
      const tableNm = tableInformation.tableNm
      if (
        this.destinationTableColumnOptions &&
        this.destinationTableColumnOptions[tableNm]
      ) {
        for (
          let i = 0;
          i < this.destinationTableColumnOptions[tableNm].length;
          i++
        ) {
          const option = this.destinationTableColumnOptions[tableNm][i]
          if (!option.value && me) {
            continue
          }
          let found = false
          if (this.migrationInformation.destinationTableInformations) {
            for (
              let j = 0;
              j < tableInformation.destinationTableColumnInformations.length;
              j++
            ) {
              const column =
                tableInformation.destinationTableColumnInformations[j]
              if (
                column.dstnColumnNm === option.value &&
                column.dstnColumnNm !== me
              ) {
                found = true
                break
              }
            }
          }
          if (found) {
            continue
          }
          options.push(option)
        }
      }
      return options
    },
    showMigrationRulesExcelUpload(e) {
      this.migrationRulesExcelFile = undefined
      this.showModal('migration-rules-excel-upload-modal')
    },
    onChangeSourceTable(columnInfo) {
      if (columnInfo.srcColumnNm || columnInfo.srcJoinColumnNm1) {
        this.changeSourceTableTarget = columnInfo
        this.showModal('changeSourceTableConfirmModalRef')
        return
      }
      columnInfo.srcTableNm = columnInfo.newSrcTableNm
      this.$forceUpdate()
    },
    migrationRulesExcelUploadConfirm(e) {
      e.preventDefault()
      this.showModal('migrationRulesExcelUploadConfirmModal')
    },
    changeSourceTable() {
      const columnInfo = this.changeSourceTableTarget
      columnInfo.srcColumnNm = ''
      columnInfo.srcJoinColumnNm1 = ''
      columnInfo.srcTableNm = columnInfo.newSrcTableNm
      this.$forceUpdate()
    },
    cancelChangeSourceTable() {
      const columnInfo = this.changeSourceTableTarget
      columnInfo.newSrcTableNm = columnInfo.srcTableNm
      this.$forceUpdate()
    },
    onChangeTable(tableInfo) {
      if (
        tableInfo.destinationTableColumnInformations &&
        tableInfo.destinationTableColumnInformations.length > 0
      ) {
        this.changeTableTarget = tableInfo
        this.showModal('changeTableConfirmModalRef')
        return
      }
      tableInfo.tableNm = tableInfo.newTableNm
      this.$forceUpdate()
    },
    changeTable() {
      const tableInfo = this.changeTableTarget
      tableInfo.destinationTableColumnInformations = []
      tableInfo.tableNm = tableInfo.newTableNm
      this.$forceUpdate()
    },
    cancelChangeTable() {
      const tableInfo = this.changeTableTarget
      tableInfo.newTableNm = tableInfo.tableNm
      this.$forceUpdate()
    },
    onChangeColumn() {
      this.$forceUpdate()
    },
    addDatabase(e) {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      this.migrationInformation.originalDatabaseInformations.push({
        databaseTyCode: '',
        validated: undefined
      })
    },
    deleteDatabase(e, index) {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      const databases = this.migrationInformation.originalDatabaseInformations
      const database = databases[index]
      if (database.id) {
        let found = false
        const tables = this.migrationInformation.destinationTableInformations
        for (let i = 0; i < tables.length; i++) {
          const table = tables[i]
          for (
            let j = 0;
            j < table.destinationTableColumnInformations.length;
            j++
          ) {
            const column = table.destinationTableColumnInformations[j]
            if (
              (column.migrationOrginlDatabaseId &&
                parseInt(column.migrationOrginlDatabaseId) ===
                  parseInt(database.id)) ||
              (column.srcJoinColumnNm2MigrationOrginlDatabaseId &&
                parseInt(column.srcJoinColumnNm2MigrationOrginlDatabaseId) ===
                  parseInt(database.id))
            ) {
              found = true
              break
            }
          }
        }
        if (found) {
          this.deleteTargetDatabase = database
          this.deleteTargetDatabaseIndex = index
          this.showModal('deleteDatabaseConfirmModalRef')
          return
        }
      }
      databases.splice(index, 1)
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    deleteDatabaseProcess() {
      const database = this.deleteTargetDatabase
      const index = this.deleteTargetDatabaseIndex
      const databases = this.migrationInformation.originalDatabaseInformations
      const tables = this.migrationInformation.destinationTableInformations
      for (let i = 0; i < tables.length; i++) {
        const table = tables[i]
        for (
          let j = table.destinationTableColumnInformations.length - 1;
          j >= 0;
          j--
        ) {
          const column = table.destinationTableColumnInformations[j]
          if (
            (column.migrationOrginlDatabaseId &&
              parseInt(column.migrationOrginlDatabaseId) ===
                parseInt(database.id)) ||
            (column.srcJoinColumnNm2MigrationOrginlDatabaseId &&
              parseInt(column.srcJoinColumnNm2MigrationOrginlDatabaseId) ===
                parseInt(database.id))
          ) {
            table.destinationTableColumnInformations.splice(j, 1)
          }
        }
      }
      databases.splice(index, 1)
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    addTable(e) {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      this.migrationInformation.destinationTableInformations.push({
        expand: true
      })
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    deleteTable(e, index) {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      this.deleteTableTargetIndex = index
      this.showModal('deleteTableConfirmModalRef')
    },
    deleteTableProcess() {
      const index = this.deleteTableTargetIndex
      this.migrationInformation.destinationTableInformations.splice(index, 1)
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    addColumn(e, table) {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      if (!table.destinationTableColumnInformations) {
        table.destinationTableColumnInformations = []
      }
      table.destinationTableColumnInformations.push({
        expand: true,
        fileFlag: 'N',
        migrationOrginlDatabaseId: '0',
        srcJoinColumnNm2MigrationOrginlDatabaseId: '0',
        srcEncptMthCode: 'migration_crypto_method.none',
        dstnEncptMthCode: 'migration_crypto_method.none'
      })
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    deleteColumn(e, table, index) {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      table.destinationTableColumnInformations.splice(index, 1)
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    addQuery(e, table, stringIndex) {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      if (!table[stringIndex]) {
        table[stringIndex] = []
      }
      table[stringIndex].push({
        text: '',
        multiline: false
      })
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    deleteQuery(e, queries, index) {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      queries.splice(index, 1)
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    canGoPrevious() {
      return this.step !== 'database-information'
    },
    canGoNext() {
      return this.step !== 'proceeding'
    },
    reenter(database) {
      if (database.id) {
        let found = false
        const tables = this.migrationInformation.destinationTableInformations
        for (let i = 0; i < tables.length; i++) {
          const table = tables[i]
          for (
            let j = 0;
            j < table.destinationTableColumnInformations.length;
            j++
          ) {
            const column = table.destinationTableColumnInformations[j]
            if (
              (column.migrationOrginlDatabaseId &&
                parseInt(column.migrationOrginlDatabaseId) ===
                  parseInt(database.id)) ||
              (column.srcJoinColumnNm2MigrationOrginlDatabaseId &&
                parseInt(column.srcJoinColumnNm2MigrationOrginlDatabaseId) ===
                  parseInt(database.id))
            ) {
              found = true
              break
            }
          }
        }
        if (found) {
          this.reenterTargetDatabase = database
          this.showModal('reenterDatabaseConfirmModalRef')
          return
        }
      }
      database.validated = undefined
      database.testConnectionMsg = ''
    },
    reenterDatabaseProcess() {
      const database = this.reenterTargetDatabase
      const tables = this.migrationInformation.destinationTableInformations
      for (let i = 0; i < tables.length; i++) {
        const table = tables[i]
        for (
          let j = table.destinationTableColumnInformations.length - 1;
          j >= 0;
          j--
        ) {
          const column = table.destinationTableColumnInformations[j]
          if (
            (column.migrationOrginlDatabaseId &&
              parseInt(column.migrationOrginlDatabaseId) ===
                parseInt(database.id)) ||
            (column.srcJoinColumnNm2MigrationOrginlDatabaseId &&
              parseInt(column.srcJoinColumnNm2MigrationOrginlDatabaseId) ===
                parseInt(database.id))
          ) {
            table.destinationTableColumnInformations.splice(j, 1)
          }
        }
      }
      database.validated = undefined
      database.testConnectionMsg = ''
    },
    savePreprocess() {
      window._eventHub.$emit('onBeginRequest')
      setTimeout(this.save, 400)
    },
    save() {
      if (
        !this.migrationInformation.originalDatabaseInformations ||
        this.migrationInformation.originalDatabaseInformations.length === 0
      ) {
        this.$toast.error(
          this.$t('migration.notification.more_than_one_db_required')
        )
        window._eventHub.$emit('onEndRequest')
        return
      }
      this.checkValidation(
        () => {
          this.saveCurrentInformation(() => {
            this.$toast.info(this.$t('common.notification.saved'))
          })
        },
        () => {
          window._eventHub.$emit('onEndRequest')
        }
      )
    },
    restoreAllExpand() {
      const tables = this.migrationInformation.destinationTableInformations
      for (let i = 0; i < tables.length; i++) {
        const table = tables[i]
        table.expand = table.originalExpand
        const columns = table.destinationTableColumnInformations
        for (let j = 0; j < columns.length; j++) {
          const column = columns[j]
          column.expand = column.originalExpand
        }
      }
      this.$forceUpdate()
    },
    closeAllExpand(cb) {
      const tables = this.migrationInformation.destinationTableInformations
      for (let i = 0; i < tables.length; i++) {
        const table = tables[i]
        table.originalExpand = table.expand
        table.expand = false
        const columns = table.destinationTableColumnInformations
        for (let j = 0; j < columns.length; j++) {
          const column = columns[j]
          column.originalExpand = column.expand
          column.expand = false
        }
      }
      this.$forceUpdate()
      this.$nextTick(() => {
        if (cb) {
          cb()
        }
      })
    },
    checkAllExpand(onSuccessCb, onFailCb, tableIndex, columnIndex) {
      if (!tableIndex) {
        tableIndex = 0
      }
      if (!columnIndex) {
        columnIndex = 0
      }
      const tables = this.migrationInformation.destinationTableInformations
      tables[tableIndex].expand = true
      this.validatingTable = tables[tableIndex].tableNm
      const columns = tables[tableIndex].destinationTableColumnInformations
      columns[columnIndex].expand = true
      this.validatingColumn = columns[columnIndex].dstnColumnNm
      this.$forceUpdate()
      this.$nextTick(() => {
        this.validateUnder(
          $(this.$el)
            .find('[step=' + this.step + ']')
            .get(0),
          '.app-body',
          undefined,
          () => {
            tables[tableIndex].expand = false
            columns[columnIndex].expand = false
            if (columnIndex + 1 === columns.length) {
              if (tableIndex + 1 === tables.length) {
                if (onSuccessCb) {
                  onSuccessCb()
                }
                this.restoreAllExpand()
                return
              }
              tableIndex++
              columnIndex = 0
            } else {
              columnIndex++
            }
            this.checkAllExpand(onSuccessCb, onFailCb, tableIndex, columnIndex)
          },
          () => {
            if (onFailCb) {
              onFailCb()
            }
          }
        )
      })
    },
    async checkValidation(onSuccessCb, onFailCb) {
      this.validating = true
      if (this.step === 'column-information') {
        this.closeAllExpand(() => {
          this.checkAllExpand(
            () => {
              this.validating = false
              if (onSuccessCb) {
                onSuccessCb()
              }
            },
            () => {
              this.validating = false
              if (onFailCb) {
                onFailCb()
              }
            }
          )
        })
      } else if (await this.checkValidationProcess()) {
        this.validating = false
        if (onSuccessCb) {
          onSuccessCb()
        }
      } else {
        this.validating = false
        if (onFailCb) {
          onFailCb()
        }
      }
    },
    async checkValidationProcess() {
      if (
        await this.validateUnder(
          $(this.$el)
            .find('[step=' + this.step + ']')
            .get(0),
          '.app-body'
        )
      ) {
        return true
      }
      return false
    },
    gotoPreviousPreprocess() {
      window._eventHub.$emit('onBeginRequest')
      setTimeout(this.gotoPrevious, 400)
    },
    gotoPrevious() {
      this.checkValidation(
        () => {
          if (this.step === 'column-information') {
            this.saveCurrentInformation(() => {
              this.gotoStep('database-information')
            })
          } else {
            window._eventHub.$emit('onEndRequest')
          }
        },
        () => {
          window._eventHub.$emit('onBeginRequest')
        }
      )
    },
    migrationRulesExcelUploadProcess(e) {
      this.$refs.migrationRulesExcelUpload.upload()
    },
    saveAndMigrationRulesExcelDownload() {
      this.saveCurrentInformation(() => {
        this.migrationRulesExcelDownload({
          id: this.savedMigrationId
        })
      })
    },
    migrationRulesExcelDownload(row) {
      this.$axios
        .get('migration/migrationRulesExcelDownload?migrationId=' + row.id, {
          responseType: 'blob'
        })
        .then(response => {
          const url = window.URL.createObjectURL(response.data)
          const link = document.createElement('a')
          link.href = url

          link.setAttribute(
            'download',
            response.headers['x-suggested-filename']
          )
          document.body.appendChild(link)
          link.click()
        })
    },
    cloneMigrationConfirm(row) {
      this.cloneTargetId = row.id
      this.showModal('cloneMigrationConfirmModalRef')
    },
    cloneMigration() {
      let url = 'migration/cloneMigration.json'
      url += '?migrationId=' + this.cloneTargetId
      this.$axios
        .$get(url, {
          dont_hide_processing: true
        })
        .then(response => {
          if (response.resultCode === 'success') {
            this.savedMigrationId = response.data.id
            this.getMigrationInformation(response.data.id, () => {
              this.gotoStep('intro', () => {
                setTimeout(() => {
                  this.gotoStep('database-information')
                }, 2000)
              })
              this.$forceUpdate()
            })
          } else {
            window._eventHub.$emit('onEndRequest')
          }
        })
    },
    cancelMigrationProcess() {
      if (this.migrationInformation && this.migrationInformation.id) {
        this.$axios
          .$put('migration/cancelMigration.json?', {
            migrationId: this.migrationInformation.id
          })
          .then(response => {
            if (response.resultCode === 'success') {
              this.migrationInformation.originalDatabaseInformations = []
              this.step = undefined
              this.$toast.info(this.$t(response.message))
            }
          })
      } else {
        this.migrationInformation.originalDatabaseInformations = []
        this.step = undefined
      }
    },
    saveCurrentInformation(cb, options) {
      this.$axios
        .$post('migration/saveMigration.json', this.migrationInformation, {
          dont_hide_processing: true
        })
        .then(response => {
          if (response.resultCode === 'success') {
            this.savedMigrationId = response.data.id
            this.getMigrationInformation(
              response.data.id,
              () => {
                this.databaseOptions = [
                  {
                    text: this.$t('migration.option.destination_database'),
                    value: '0'
                  }
                ]
                for (
                  let i = 0;
                  i <
                  this.migrationInformation.originalDatabaseInformations.length;
                  i++
                ) {
                  const databaseInformation = this.migrationInformation
                    .originalDatabaseInformations[i]
                  databaseInformation.validated = 'success'
                  this.databaseOptions.push({
                    text:
                      databaseInformation.databaseAlias +
                      ' - ' +
                      databaseInformation.databaseHost +
                      ':' +
                      databaseInformation.databasePort +
                      '/' +
                      databaseInformation.databaseDbName,
                    value: databaseInformation.id
                  })
                }
                for (
                  let i = 0;
                  i <
                  this.migrationInformation.destinationTableInformations.length;
                  i++
                ) {
                  const table = this.migrationInformation
                    .destinationTableInformations[i]
                  table.newTableNm = table.tableNm
                  for (
                    let j = 0;
                    j < table.destinationTableColumnInformations.length;
                    j++
                  ) {
                    const column = table.destinationTableColumnInformations[j]
                    column.newSrcTableNm = column.srcTableNm
                  }
                }
                if (cb) {
                  cb()
                }
              },
              options
            )
          } else {
            window._eventHub.$emit('onEndRequest')
          }
        })
    },
    gotoNextPreprocess() {
      window._eventHub.$emit('onBeginRequest')
      setTimeout(this.gotoNext, 400)
    },
    gotoNext() {
      this.checkValidation(
        () => {
          if (this.step === 'database-information') {
            this.gotoColumnInformation()
          } else if (this.step === 'column-information') {
            this.saveCurrentInformation(
              () => {
                this.migrate()
              },
              { dont_hide_processing: true }
            )
          } else {
            window._eventHub.$emit('onEndRequest')
          }
        },
        () => {
          window._eventHub.$emit('onEndRequest')
        }
      )
    },
    migrate() {
      this.showModal('migrateConfirmModalRef')
    },
    migrateProcess() {
      this.$axios
        .$post('migration/migrate.json', {
          menuAlias: this.getMenuAlias(),
          migrationId: this.savedMigrationId
        })
        .then(response => {
          if (response.resultCode === 'success') {
            this.sttusCode = 'migration_status.in_progress'
          }
        })
    },
    gotoColumnInformation() {
      if (
        !this.migrationInformation.originalDatabaseInformations ||
        this.migrationInformation.originalDatabaseInformations.length === 0
      ) {
        this.$toast.error(
          this.$t('migration.notification.more_than_one_db_required')
        )
        window._eventHub.$emit('onEndRequest')
        return
      }
      const databases = this.migrationInformation.originalDatabaseInformations
      for (let i = 0; i < databases.length; i++) {
        for (let j = i + 1; j < databases.length; j++) {
          if (databases[i].databaseAlias === databases[j].databaseAlias) {
            this.$toast.error(
              this.$t('migration.notification.same_alias_exists')
            )
            const invalidInputControlName = $(
              '[database-index=' + i + '] [desc=database-alias]'
            ).get(0)
            const scrollElem = '.app-body'
            const relative =
              $(scrollElem).scrollTop() + $(scrollElem).offset().top
            $(scrollElem).animate(
              {
                scrollTop:
                  $(invalidInputControlName).offset().top + relative - 150
              },
              400,
              'swing',
              () => {
                $(invalidInputControlName).animate(
                  {
                    opacity: 0.4
                  },
                  200,
                  'swing',
                  () => {
                    $(invalidInputControlName).animate(
                      {
                        opacity: 1
                      },
                      200,
                      'swing'
                    )
                  }
                )
              }
            )
            $(invalidInputControlName)
              .find('input[type!=hidden]')
              .focus()
            return
          }
        }
      }
      const gotoDataBaseInformationFunc = () => {
        if (
          $(this.$el).find(
            '.original-database-list-item[connection-validated=fail]'
          ).length > 0
        ) {
          const scrollElem = '.app-body'
          const relative =
            $(scrollElem).scrollTop() + $(scrollElem).offset().top
          const failedDbControl = $(this.$el)
            .find('.original-database-list-item[connection-validated=fail]')
            .get(0)
          $(scrollElem).animate(
            {
              scrollTop: $(failedDbControl).offset().top + relative - 150
            },
            400,
            'swing',
            () => {
              $(failedDbControl).animate(
                {
                  opacity: 0.4
                },
                200,
                'swing',
                () => {
                  $(failedDbControl).animate(
                    {
                      opacity: 1
                    },
                    200,
                    'swing'
                  )
                }
              )
            }
          )
          this.$toast.error(
            this.$t('migration.notification.connection_failed_db_exist')
          )
          return
        }
        this.saveCurrentInformation(
          () => {
            this.$axios
              .$get(
                'migration/getMigrationDatabaseInfos.json?' +
                  this.getMenuAliasQuery() +
                  '&migrationId=' +
                  this.migrationInformation.id
              )
              .then(response => {
                if (response.resultCode === 'success') {
                  this.destinationTableOptions = [
                    {
                      text: this.$t('common.placeholder.select'),
                      value: ''
                    }
                  ]
                  this.originalDatabaseTableOptions = {}
                  this.originalDatabaseTableColumnOptions = {}
                  const odtOptions = this.originalDatabaseTableOptions
                  const odtcOptions = this.originalDatabaseTableColumnOptions
                  odtOptions[0] = {}
                  odtcOptions[0] = {}
                  if (response.data) {
                    if (response.data.originalDatabases) {
                      for (
                        let i = 0;
                        i < response.data.originalDatabases.length;
                        i++
                      ) {
                        const database = response.data.originalDatabases[i]
                        const id = database.info.id
                        const tableOptions = [
                          {
                            text: this.$t('common.placeholder.select'),
                            value: ''
                          }
                        ]
                        odtcOptions[id] = {}
                        for (let j = 0; j < database.tables.length; j++) {
                          const table = database.tables[j]
                          tableOptions.push({
                            text: table.name,
                            value: table.name
                          })
                          const columnOptions = [
                            {
                              text: this.$t('common.placeholder.select'),
                              value: ''
                            }
                          ]
                          if (table.columns) {
                            for (let j = 0; j < table.columns.length; j++) {
                              columnOptions.push({
                                text: table.columns[j],
                                value: table.columns[j]
                              })
                            }
                          }
                          odtcOptions[id][table.name] = columnOptions
                        }
                        odtOptions[id] = tableOptions
                      }
                    }
                    this.destinationTableColumnOptions = {}
                    const dtcOptions = this.destinationTableColumnOptions
                    if (response.data.destinationTables) {
                      for (
                        let i = 0;
                        i < response.data.destinationTables.length;
                        i++
                      ) {
                        const table = response.data.destinationTables[i]
                        this.destinationTableOptions.push({
                          text: table.name,
                          value: table.name
                        })
                        const columnOptions = [
                          {
                            text: this.$t('common.placeholder.select'),
                            value: ''
                          }
                        ]
                        if (table.columns) {
                          for (let j = 0; j < table.columns.length; j++) {
                            columnOptions.push({
                              text: table.columns[j],
                              value: table.columns[j]
                            })
                          }
                        }
                        dtcOptions[table.name] = columnOptions
                        odtcOptions[0][table.name] = columnOptions
                      }
                    }
                    odtOptions[0] = this.destinationTableOptions
                  }
                  this.gotoStep('column-information')
                }
              })
          },
          {
            dont_hide_processing: true
          }
        )
      }
      let untestedExists = false
      $(this.$el)
        .find('.original-database-list-item')
        .each(function() {
          if (!$(this).attr('connection-validated')) {
            untestedExists = true
            return false
          }
        })
      if (
        untestedExists &&
        $(this.$el).find(
          '.original-database-list-item[connection-validated=fail]'
        ).length === 0
      ) {
        this.$toast.info(this.$t('migration.notification.auto_test'))
        this.autoTestConnection(gotoDataBaseInformationFunc)
      } else {
        gotoDataBaseInformationFunc()
      }
    },
    startMigration() {
      this.migrationInformation.originalDatabaseInformations = []
      // test
      /*
      this.migrationInformation.originalDatabaseInformations.push({
        databaseTyCode: 'database_type.mariadb',
        databaseAlias: 'db1',
        databaseHost: 'db1.mylamper.com',
        databasePort: '3306',
        databaseDbName: 'archi5ive_cms',
        databaseAditParamtr:
          '?autoReconnect=true&amp;useSSL=false&amp;characterEncoding=UTF-8',
        databaseUserId: 'archi5ive',
        databasePasswd: 'archi5ive2134!',
        validated: undefined
      })
      this.migrationInformation.originalDatabaseInformations.push({
        databaseTyCode: 'database_type.mssql',
        databaseAlias: 'db2',
        databaseHost: '101.55.17.182',
        databasePort: '9597',
        databaseDbName: 'PSM_TCS',
        databaseUserId: 'PSM',
        databasePasswd: '1234QWer',
        validated: undefined
      }) */
      // test end
      this.gotoStep('intro', () => {
        setTimeout(() => {
          this.gotoStep('database-information')
        }, 2000)
      })
    },
    autoTestConnection(cb) {
      for (
        let i = 0;
        i < this.migrationInformation.originalDatabaseInformations.length;
        i++
      ) {
        const databaseInformation = this.migrationInformation
          .originalDatabaseInformations[i]
        if (!databaseInformation.validated) {
          this.testConnection(undefined, databaseInformation, i, () => {
            this.$nextTick(() => {
              let untestedExists = false
              $(this.$el)
                .find('.original-database-list-item')
                .each(function() {
                  if (!$(this).attr('connection-validated')) {
                    untestedExists = true
                    return false
                  }
                })
              if (!untestedExists && cb) {
                cb()
              }
            })
          })
        }
      }
    },
    testConnection(e, databaseInformation, databaseInformationIndex, cb) {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      window._eventHub.$emit('onBeginRequest')
      this.checkValidation(
        () => {
          databaseInformation.testingConnection = true
          this.$axios
            .$post('migration/testConnection.json', databaseInformation)
            .then(response => {
              databaseInformation.testingConnection = false
              databaseInformation.validated = 'fail'
              if (response.resultCode === 'success') {
                if (response.data) {
                  databaseInformation.validated = response.data.result
                  databaseInformation.testConnectionMsg = response.data.msg
                  this.$forceUpdate()
                }
                if (cb) {
                  cb()
                }
              }
            })
            .catch(() => {
              databaseInformation.testingConnection = false
              databaseInformation.validated = 'fail'
            })
        },
        () => {
          window._eventHub.$emit('onEndRequest')
        }
      )
    },
    gotoStep(step, cb) {
      const stepFunc = () => {
        this.step = step
        this.$validator.reset()
        this.$forceUpdate()
        this.$nextTick(() => {
          $(this.$el)
            .find('.step-area')
            .fadeIn()
          if (cb) {
            cb()
          }
        })
      }
      if (this.step) {
        $(this.$el)
          .find('.step-area')
          .fadeOut(400, 'linear', stepFunc)
      } else {
        stepFunc()
      }
    },
    getMigrationInformation(migrationId, cb, options) {
      this.gettingDatabaseInformation = true
      this.$axios
        .$get(
          'migration/getMigrationInformation.json?' +
            this.getMenuAliasQuery() +
            '&migrationId=' +
            migrationId,
          options
        )
        .then(response => {
          if (response.resultCode === 'success') {
            this.migrationInformation = response.data
            const migration = this.migrationInformation
            const tables = migration.destinationTableInformations
            if (tables) {
              for (let i = 0; i < tables.length; i++) {
                const table = tables[i]
                const columns = table.destinationTableColumnInformations
                if (columns) {
                  for (let j = 0; j < columns.length; j++) {
                    columns[j].migrationOrginlDatabaseId =
                      '' + columns[j].migrationOrginlDatabaseId
                  }
                }
              }
            }
            if (cb) {
              cb()
            }
          }
          this.gettingDatabaseInformation = false
        })
        .catch(() => {
          this.gettingDatabaseInformation = false
        })
    },
    pollLatestMigration() {
      this.$axios
        .$get(
          'migration/getMigrationHistory.json?' + this.getMenuAliasQuery(),
          {
            no_loading: true,
            dont_hide_processing: true
          }
        )
        .then(response => {
          if (response.resultCode === 'success') {
            this.lastFinishedMigration = response.data.lastFinishedMigration
            this.lastMigration = response.data.lastMigration
            if (response.data && response.data.lastMigration) {
              const oldSttusCode = (this.oldSttusCode = this.sttusCode)
              this.sttusCode = 'migration_status.none'
              this.latestMigrationStatusMessage =
                response.data.lastMigration.sttusMsg
              this.latestMigrationResultMessage =
                response.data.lastMigration.result
              if (
                response.data.lastMigration.sttusCode ===
                  'migration_status.writing_information' ||
                response.data.lastMigration.sttusCode ===
                  'migration_status.in_progress'
              ) {
                this.sttusCode = response.data.lastMigration.sttusCode
              }

              if (
                this.fileUploaded ||
                (!this.gettingDatabaseInformation &&
                  !this.migrationInformation.id &&
                  this.sttusCode === 'migration_status.writing_information')
              ) {
                this.fileUploaded = false
                this.getMigrationInformation(
                  response.data.lastMigration.id,
                  () => {
                    this.gotoStep('database-information')
                    this.$forceUpdate()
                  }
                )
              }
              if (
                (this.sttusCode !== 'migration_status.writing_information' &&
                  this.sttusCode !== 'migration_status.none') ||
                ((oldSttusCode === 'migration_status.writing_information' ||
                  oldSttusCode === 'migration_status.in_progress') &&
                  (response.data.sttusCode === 'migration_status.cancel' ||
                    response.data.sttusCode === 'migration_status.success' ||
                    response.data.sttusCode === 'migration_status.fail'))
              ) {
                this.step = undefined
              }
            } else {
              this.sttusCode = 'migration_status.none'
            }
          }
          if (this.pollTimerId) {
            clearTimeout(this.pollTimerId)
            this.pollTimerId = undefined
          }
          if (this.sttusCode === 'migration_status.in_progress') {
            this.pollTimerId = setTimeout(this.pollLatestMigration, 1000)
          } else {
            this.pollTimerId = setTimeout(this.pollLatestMigration, 3000)
          }
        })
        .catch(error => {
          if (this.pollTimerId) {
            clearTimeout(this.pollTimerId)
            this.pollTimerId = undefined
          }
          if (error.response.status !== 401 && error.response.status !== 403) {
            this.pollTimerId = setTimeout(this.pollLatestMigration, 3000)
          }
        })
    }
  }
}
</script>

<style scoped>
.migration-button-area {
  text-align: right;
  padding-top: 20px;
}
.start-migration-guide {
  text-align: center;
  font-size: 1.5rem;
  font-weight: bold;
  padding: 200px 0;
}
.step-area {
  display: none;
}
.step-title {
  font-size: 1.3rem;
  padding: 10px 0 5px;
}
.step-guide {
  font-size: 0.85rem;
  padding: 5px 0;
}
.item-button-area {
  padding: 15px 0;
  text-align: right;
}
.column-source-information,
.column-destination-information,
.table-list-item,
.column-list-item,
.original-database-list-item {
  border: 1px solid #cccccc;
  border-radius: 5px;
  margin-bottom: 15px;
  padding: 20px;
}
.expand-toggle-button {
  padding: 10px;
  float: right;
  margin-top: -10px;
}
.table-detail-area {
  margin-top: 20px;
}
.column-title,
.table-title {
  cursor: pointer;
  height: 40px;
  background-color: #efefef;
  padding: 10px 5px;
}
.no-query,
.no-table,
.no-column {
  padding: 80px 0;
  text-align: center;
}
.no-query {
  padding: 20px;
}
.no-database {
  padding: 200px 0;
  text-align: center;
}
.test-connection-button {
  margin-left: 20px;
}
.test-connection-result {
  text-align: right;
  padding-top: 15px;
}
.step-control-area {
  margin-top: 10px;
  border: 1px solid #cccccc;
  padding: 10px;
  width: 100%;
  -webkit-box-shadow: 5px 5px 7px rgba(0, 0, 0, 0.3);
  -moz-box-shadow: 5px 5px 7px rgba(0, 0, 0, 0.3);
  box-shadow: 5px 5px 7px rgba(0, 0, 0, 0.3);
}
.previous-button-area {
  text-align: left;
}
.next-button-area {
  text-align: right;
}
.excel-button-area {
  text-align: right;
  margin-bottom: 10px;
}
.latest-migration-status,
.latest-migration-result {
  margin-top: 10px;
  margin-bottom: 10px;
}
.latest-migration-status-title,
.latest-migration-result-title {
  font-weight: bold;
}
.latest-migration-result-content {
  border: 1px solid #cccccc;
  border-radius: 5px;
  padding: 10px;
}
.migration-status {
  font-weight: bold;
  margin-bottom: 10px;
}
.writing-information-area {
  padding-bottom: 118px;
}
.expand-advanced-button-area {
  margin: 15px 0;
  text-align: right;
}
.form-actions {
  position: -webkit-sticky;
  position: sticky;
  bottom: 0;
  padding: 10px;
  background: white;
  border-radius: 5px;
  border: 1px solid #dddddd;
  float: right;
  z-index: 10;
}
.query-area {
  margin-bottom: 20px;
  border: 1px solid #cccccc;
  padding: 15px;
}
.query-textarea {
  overflow: hidden;
  resize: none;
}
.query-item {
  margin-bottom: 5px;
}
.add-query-area {
  margin-top: 10px;
}
.validation-status {
  float: left;
}
label {
  margin-top: 15px;
}
</style>
